import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';
import WhatsAppSendButton from '../common/WhatsAppSendButton';

const GREEN = '#1b4332';
const BLUE  = '#1565c0';
const RED   = '#dc2626';

const HDR_SX = {
  fontWeight: 700, color: '#fff', fontSize: '0.72rem', textTransform: 'uppercase',
  backgroundColor: '#2d3748',
  borderRight: '1px solid rgba(255,255,255,0.15)', py: 1.0, px: 0.8,
  whiteSpace: 'nowrap', textAlign: 'center',
};
const CELL_SX = { py: 0.8, px: 0.8, fontSize: '0.76rem', textAlign: 'center' };

function fmtNum(v, d = 0) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}
function fmtDate(val) {
  if (!val) return '—';
  const s = String(val).substring(0, 10);
  const [yr, mo, dy] = s.split('-');
  return `${dy}/${mo}/${yr}`;
}
function fmtWait(diffMinutes) {
  // diffMinutes is negative (time until ASSEMBLYSTART deadline)
  // More negative = been waiting longer
  if (diffMinutes === null || diffMinutes === undefined) return '—';
  const abs = Math.abs(parseFloat(diffMinutes));
  const h = Math.floor(abs / 60);
  const m = Math.floor(abs % 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

const COLS = [
  'SR', 'LOT NO', 'CP NAME', 'TABLE', 'ASSEMBLER',
  'SIZE', 'THICK', 'GLUE', 'QUALITY', 'SHIFT',
  'ASSY DATE', 'START', 'END', 'PCS', 'CBM', 'WAITED', 'HP STATUS'
];

const ColdPressPendingView = ({ onBackClick, user }) => {
  const [date, setDate]       = useState(null);
  const [shift, setShift]     = useState('DAY');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [data, setData]       = useState(null);   // data[0] = detail rows, data[1] = summary

  const fetchData = async () => {
    if (!date) { setError('Please select a date'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API_URL}/order/coldpresspending`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: date.format('YYYY-MM-DD'), shift }),
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.data) throw new Error('No data returned');
      setData(apiData.data);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    if (data[0]?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[0]), 'Cold Press Pending');
    if (data[1]?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[1]), 'Summary');
    XLSX.writeFile(wb, `ColdPressPending_${date.format('YYYY-MM-DD')}_${shift}.xlsx`);
  };

  const rows    = data?.[0] || [];
  const summary = data?.[1]?.[0] || null;

  // shift label for display
  const shiftLabel = shift === 'DAY'
    ? `Day Shift (07:00 – 19:00)`
    : shift === 'NIGHT'
    ? `Night Shift (19:00 – 07:00 next day)`
    : `Full Day (07:00 – 07:00 next day)`;

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 8, px: { xs: 1, md: 2 } }}>

      {/* Search card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(27,67,50,0.1)', border: '1px solid #e8f5e9' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: GREEN }}><ArrowBackIcon /></IconButton>
            )}
            <Box sx={{ background: `linear-gradient(135deg, ${GREEN} 0%, #2d6a4f 100%)`, borderRadius: 2, p: 1.5, mr: 2 }}>
              <HourglassEmptyIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: GREEN }}>Hotpress Shift Handover</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Assembly lots pending hotpress — {shiftLabel}
              </Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center" flexWrap="wrap">
              <DatePicker
                label="Date"
                value={date}
                onChange={setDate}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
              />
              <ToggleButtonGroup
                value={shift}
                exclusive
                onChange={(_, v) => { if (v) setShift(v); }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 3 },
                  '& .Mui-selected': { backgroundColor: `${GREEN} !important`, color: '#fff !important' },
                }}
              >
                <ToggleButton value="DAY">DAY</ToggleButton>
                <ToggleButton value="NIGHT">NIGHT</ToggleButton>
                <ToggleButton value="BOTH">BOTH</ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained" size="large" startIcon={<SearchIcon />}
                onClick={fetchData} disabled={loading}
                sx={{ minWidth: 130, backgroundColor: GREEN, '&:hover': { backgroundColor: '#0f2419' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Search
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={6}><CircularProgress sx={{ color: GREEN }} /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <>
          {/* Summary strip */}
          {summary && (
            <Card sx={{
              mb: 3, borderRadius: 2,
              background: `linear-gradient(135deg, ${GREEN} 0%, #2d6a4f 100%)`,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box display="flex" flexWrap="wrap" gap={4} alignItems="center">
                  <Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' }}>
                      Total Lots
                    </Typography>
                    <Typography sx={{ color: '#fff', fontSize: '1.8rem', fontWeight: 800, lineHeight: 1 }}>
                      {summary.TOTAL_LOTS || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '1px', height: 40, background: 'rgba(255,255,255,0.2)', display: { xs: 'none', md: 'block' } }} />
                  <Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' }}>
                      HP Pending
                    </Typography>
                    <Typography sx={{ color: '#fef08a', fontSize: '1.8rem', fontWeight: 800, lineHeight: 1 }}>
                      {summary.HP_PENDING || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '1px', height: 40, background: 'rgba(255,255,255,0.2)', display: { xs: 'none', md: 'block' } }} />
                  <Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' }}>
                      HP Done
                    </Typography>
                    <Typography sx={{ color: '#a7f3d0', fontSize: '1.8rem', fontWeight: 800, lineHeight: 1 }}>
                      {summary.HP_DONE || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '1px', height: 40, background: 'rgba(255,255,255,0.2)', display: { xs: 'none', md: 'block' } }} />
                  <Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' }}>
                      Total PCS
                    </Typography>
                    <Typography sx={{ color: '#fff', fontSize: '1.8rem', fontWeight: 800, lineHeight: 1 }}>
                      {fmtNum(summary.TOTAL_PCS, 0)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '1px', height: 40, background: 'rgba(255,255,255,0.2)', display: { xs: 'none', md: 'block' } }} />
                  <Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' }}>
                      Total CBM
                    </Typography>
                    <Typography sx={{ color: '#fff', fontSize: '1.4rem', fontWeight: 700, lineHeight: 1 }}>
                      {fmtNum(summary.TOTAL_CBM, 3)}
                    </Typography>
                  </Box>
                  <Box sx={{ ml: 'auto' }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' }}>
                      {date?.format('DD/MM/YYYY')} · {shift} SHIFT
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Download + table */}
          <Box display="flex" justifyContent="flex-end" mb={1} gap={1}>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
              sx={{ color: GREEN, fontWeight: 600, textTransform: 'none', border: `1px solid ${GREEN}40`, borderRadius: 1, px: 2 }}>
              Download Excel
            </Button>
            <Button size="small" variant="outlined"
              disabled={!rows.length}
              onClick={() => {
                const url = `${API_URL}/api/hotpress-handover/preview?date=${date.format('YYYY-MM-DD')}&shift=${shift}`;
                window.open(url, '_blank');
              }}
              sx={{ color: GREEN, fontWeight: 600, textTransform: 'none', border: `1px solid ${GREEN}40`, borderRadius: 1, px: 2 }}>
              Preview Image
            </Button>
          </Box>

          {rows.length === 0 ? (
            <Card sx={{ p: 6, textAlign: 'center', borderRadius: 3, background: '#fafafa', boxShadow: 'none', border: '2px dashed #e0e0e0' }}>
              <Typography variant="h6" sx={{ color: '#666' }}>No pending lots for this shift</Typography>
              <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>All assembly lots have been hot-pressed</Typography>
            </Card>
          ) : (
            <Card sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              {/* Table header bar */}
              <Box sx={{ background: GREEN, px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.88rem' }}>
                  Pending Assembly Lots
                </Typography>
                <Box sx={{ background: 'rgba(255,255,255,0.18)', px: 1.2, py: 0.2, borderRadius: 1 }}>
                  <Typography sx={{ color: '#fff', fontSize: '0.72rem', fontWeight: 600 }}>
                    {rows.length} lots
                  </Typography>
                </Box>
              </Box>

              <TableContainer sx={{ maxHeight: 560, overflowX: 'auto' }}>
                <Table stickyHeader size="small" sx={{ minWidth: 1100 }}>
                  <TableHead>
                    <TableRow>
                      {COLS.map(h => <TableCell key={h} sx={HDR_SX}>{h}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, i) => (
                      <TableRow key={i} sx={{
                        backgroundColor: i % 2 === 0 ? '#fff' : '#f8faf9',
                        '&:hover': { backgroundColor: '#e8f5e9' },
                      }}>
                        <TableCell sx={CELL_SX}>{row.row_num ?? i + 1}</TableCell>
                        <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: BLUE }}>{row.LOT_NUMBER || '—'}</TableCell>
                        <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: GREEN }}>{row.CPNAME || '—'}</TableCell>
                        <TableCell sx={CELL_SX}>{row.ASSEMBLY_TABLENO || '—'}</TableCell>
                        <TableCell sx={{ ...CELL_SX, textAlign: 'left', px: 1 }}>{row.ASSEMBLER_NAME || '—'}</TableCell>
                        <TableCell sx={CELL_SX}>{row.SIZE || '—'}</TableCell>
                        <TableCell sx={CELL_SX}>{fmtNum(row.THICKNESS, 2)}</TableCell>
                        <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: GREEN }}>{row.GLUETYPE || '—'}</TableCell>
                        <TableCell sx={CELL_SX}>{row.QUALITY || '—'}</TableCell>
                        <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: row.SHIFT === 'NIGHT' ? BLUE : '#2e7d32' }}>
                          {row.SHIFT || '—'}
                        </TableCell>
                        <TableCell sx={CELL_SX}>{fmtDate(row.ASSEMBLY_DATE)}</TableCell>
                        <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{row.ASSEMBLYSTART || '—'}</TableCell>
                        <TableCell sx={CELL_SX}>{row.ASSEMBLYEND || '—'}</TableCell>
                        <TableCell sx={{ ...CELL_SX, fontWeight: 700, color: '#2e7d32' }}>{fmtNum(row.PCS, 0)}</TableCell>
                        <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: GREEN }}>{fmtNum(row.CBM, 3)}</TableCell>
                        <TableCell sx={{ ...CELL_SX, fontWeight: 700, color: RED }}>
                          {fmtWait(row.DIFF_MINUTES)}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', py: 0.6, px: 0.5 }}>
                          {row.IS_HP === 1 || row.IS_HP === '1'
                            ? <Box sx={{ background: '#dcfce7', color: GREEN, fontSize: '0.65rem', fontWeight: 700, px: 0.8, py: 0.2, borderRadius: 0.5, display: 'inline-block' }}>HP DONE</Box>
                            : <Box sx={{ background: '#fef9c3', color: '#854d0e', fontSize: '0.65rem', fontWeight: 700, px: 0.8, py: 0.2, borderRadius: 0.5, display: 'inline-block' }}>PENDING</Box>
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}

        </>
      )}

      {!data && !loading && !error && (
        <Card sx={{ p: 8, textAlign: 'center', borderRadius: 3, background: '#fafafa', boxShadow: 'none', border: '2px dashed #e0e0e0' }}>
          <HourglassEmptyIcon sx={{ fontSize: 60, color: '#bdbdbd', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>No data to display</Typography>
          <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>Select a date and shift, then click Search</Typography>
        </Card>
      )}

      {data && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <WhatsAppSendButton
            user={user}
            label={`Send Handover ${shift} Report to WhatsApp`}
            apiUrl={date ? `${API_URL}/api/hotpress-handover/send` : null}
            payload={{ date: date?.format('YYYY-MM-DD'), shift }}
            disabled={!date}
          />
        </Box>
      )}
    </Container>
  );
};

export default ColdPressPendingView;
